import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { ScrollArea } from '../ui/scroll-area';
import { FolderOpen, Search, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Many assets × many destinations in one action.
 *
 * The old dialog handed a batch to exactly one target. On a real mobilisation
 * the same van load goes to two sites and three fitters, so this one takes a
 * list on both sides and writes every pairing at once. A crew can hold kit
 * jointly while one named person carries the can for it.
 */
const TargetPicker = ({ label, icon: Icon, items, selected, onToggle, labelOf, testId }) => {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((i) => labelOf(i).toLowerCase().includes(needle));
  }, [items, q, labelOf]);

  return (
    <div className="min-w-0">
      <div className="flex items-center justify-between mb-1">
        <Label className="text-xs flex items-center gap-1">
          <Icon className="w-3.5 h-3.5" /> {label}
        </Label>
        <Badge variant="secondary" className="text-[10px]">{t('inv.matrix.picked', { count: selected.length })}</Badge>
      </div>
      <div className="relative mb-1">
        <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('inv.matrix.filter')}
          className="h-8 pl-7 text-xs" data-testid={`${testId}-filter`} />
      </div>
      <ScrollArea className="h-[180px] rounded-md border p-1.5">
        {visible.length === 0 ? (
          <p className="py-6 text-center text-xs text-muted-foreground">{t('inv.matrix.noMatch')}</p>
        ) : visible.map((item) => (
          <label key={item.id}
            className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-muted/60"
            data-testid={`${testId}-option-${item.id}`}>
            <Checkbox checked={selected.includes(item.id)} onCheckedChange={() => onToggle(item.id)}
              data-testid={`${testId}-check-${item.id}`} />
            <span className="min-w-0 flex-1 truncate">{labelOf(item)}</span>
          </label>
        ))}
      </ScrollArea>
    </div>
  );
};

const MatrixAssignDialog = ({ open, onOpenChange, assetIds = [], projects = [], employees = [], onDone }) => {
  const { t } = useTranslation();
  const [projectIds, setProjectIds] = useState([]);
  const [employeeIds, setEmployeeIds] = useState([]);
  const [responsible, setResponsible] = useState('');
  const [groupName, setGroupName] = useState('');
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState('');
  const [ongoing, setOngoing] = useState(true);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const toggle = (setter) => (id) =>
    setter((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const projectLabel = (p) => [p.project_number, p.name].filter(Boolean).join(' — ');
  const employeeLabel = (e) => e.name || e.employee_code || e.id;

  const pairs = assetIds.length * (projectIds.length + employeeIds.length);

  const submit = async () => {
    if (!projectIds.length && !employeeIds.length) {
      toast.error(t('inv.matrix.pickOne'));
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/inventory/assets/matrix-assign`, {
        asset_ids: assetIds,
        project_ids: projectIds,
        employee_ids: employeeIds,
        responsible_employee_id: responsible || null,
        group_name: groupName || null,
        date_from: dateFrom || null,
        date_to: ongoing ? null : (dateTo || null),
        ongoing,
        notes: notes || null,
      });
      toast.success(t('inv.matrix.written', { assignments: r.data.assignments_created, assets: r.data.assets }));
      onOpenChange(false);
      setProjectIds([]);
      setEmployeeIds([]);
      setGroupName('');
      setNotes('');
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('inv.matrix.failed'));
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="matrix-assign-dialog">
        <DialogHeader>
          <DialogTitle>{t('inv.matrix.title', { count: assetIds.length })}</DialogTitle>
          <DialogDescription>{t('inv.matrix.hint')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TargetPicker label={t('inv.matrix.projects')} icon={FolderOpen} items={projects}
              selected={projectIds} onToggle={toggle(setProjectIds)}
              labelOf={projectLabel} testId="matrix-projects" />
            <TargetPicker label={t('inv.matrix.employees')} icon={Users} items={employees}
              selected={employeeIds} onToggle={toggle(setEmployeeIds)}
              labelOf={employeeLabel} testId="matrix-employees" />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs">{t('inv.matrix.crewName')}</Label>
              <Input value={groupName} onChange={(e) => setGroupName(e.target.value)}
                placeholder={t('inv.matrix.crewPlaceholder')} className="h-9" data-testid="matrix-group-name" />
            </div>
            <div>
              <Label className="text-xs">{t('inv.matrix.responsible')}</Label>
              <Select value={responsible} onValueChange={setResponsible}>
                <SelectTrigger className="h-9" data-testid="matrix-responsible">
                  <SelectValue placeholder={t('inv.matrix.nobodyNamed')} />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{employeeLabel(e)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs">{t('inv.common.from')}</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                className="h-9" data-testid="matrix-date-from" />
            </div>
            <div>
              <Label className="text-xs">{t('inv.common.until')}</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                disabled={ongoing} className="h-9" data-testid="matrix-date-to" />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Checkbox checked={ongoing} onCheckedChange={(v) => setOngoing(!!v)} data-testid="matrix-ongoing" />
            {t('inv.common.ongoingNoEnd')}
          </label>

          <div>
            <Label className="text-xs">{t('inv.common.note')}</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              placeholder={t('inv.matrix.notePlaceholder')} data-testid="matrix-notes" />
          </div>

          {pairs > 0 && (
            <p className="text-xs text-muted-foreground" data-testid="matrix-preview">
              {t('inv.matrix.preview', { count: pairs })}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="matrix-cancel">{t('common.cancel')}</Button>
          <Button onClick={submit} disabled={saving || pairs === 0} data-testid="matrix-submit">
            {saving ? t('inv.matrix.assigning') : t('inv.matrix.writeCount', { count: pairs })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MatrixAssignDialog;
